import { type ReactNode, useState, type MouseEvent, useRef, useEffect } from 'react';
import {
  Stack,
  Box,
  Typography,
  IconButton,
  Collapse,
  Divider,
  type SxProps,
  type Theme,
  useTheme,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useCollapsibleGroup } from './collapsible-group.context';
import { useTranslations } from '@/i18n/use-translations';
import {
  FLOW_CONTROL_MEDIA_ACTION_SLOT_WIDTH_PX,
  FLOW_CONTROL_MEDIA_PLAY_SLOT_WIDTH_PX,
  flowControlActionsRailWidthPx,
} from '@/components/flows/detail/flow-detail.types';

/** Match default media-playlist row height (`minHeight: 40`). */
const FLOW_CONTROL_ROW_HEIGHT_PX = 40;

const iconButtonIconSx = {
  '& > svg': {
    fontSize: '1.75rem',
    width: '1.75rem',
    height: '1.75rem',
  },
} as const;

const flowControlActionSlotSx = {
  width: FLOW_CONTROL_MEDIA_ACTION_SLOT_WIDTH_PX,
  minWidth: FLOW_CONTROL_MEDIA_ACTION_SLOT_WIDTH_PX,
  maxWidth: FLOW_CONTROL_MEDIA_ACTION_SLOT_WIDTH_PX,
  height: FLOW_CONTROL_ROW_HEIGHT_PX,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  overflow: 'hidden',
} as const;

export interface IconButtonConfig {
  icon: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  testId?: string;
  tooltip?: string;
  color?: 'info' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'default';
  sx?: SxProps<Theme>;
  /** When true, `icon` is already an interactive control and must not be wrapped in IconButton. */
  unwrapped?: boolean;
}

export interface CollapsibleBoxProps {
  id?: string; // For use with CollapsibleGroup
  /** Prepended inside the header row (e.g. playlist reorder handle). */
  leadingAdornment?: ReactNode;
  iconStart?: ReactNode;
  iconsEnd?: ReactNode[];
  actionButtons?: IconButtonConfig[];
  title?: string | ReactNode;
  /** Icon action in the header row (e.g. playlist advance mode). Prefer `headerActions` for multiple. */
  headerAction?: IconButtonConfig;
  /** Secondary header actions left of play (flow Control: Next show-mode, then advance/display). */
  headerActions?: IconButtonConfig[];
  isEditingTitle?: boolean;
  children?: string | ReactNode;
  onClick?: (event: MouseEvent) => void;
  sx?: SxProps<Theme>;
  // Controlled mode props (for standalone use)
  expanded?: boolean;
  onToggle?: (event: MouseEvent) => void;
  showCollapseArrow?: boolean;
  collapseIcon?: ReactNode;
  disableCollapse?: boolean;
  /** When true, the default expand/collapse control has no tooltip (e.g. contributor rows). */
  hideCollapseExpandTooltip?: boolean;
  /** Play action rendered after a vertical divider (e.g. flow Control tab media rows). */
  playActionButton?: IconButtonConfig;
  /** Fixed width for trailing actions (flow Control media rows — aligns virtual + real). */
  actionsRailWidth?: number;
}

export const CollapsibleBox = ({
  id,
  leadingAdornment,
  iconStart,
  iconsEnd = [],
  actionButtons = [],
  title,
  headerAction,
  headerActions,
  isEditingTitle = false,
  children,
  onClick,
  sx,
  expanded: controlledExpanded,
  onToggle,
  showCollapseArrow = true,
  collapseIcon,
  disableCollapse = false,
  hideCollapseExpandTooltip = false,
  playActionButton,
  actionsRailWidth,
}: CollapsibleBoxProps) => {
  const t = useTranslations();
  const theme = useTheme();
  const [internalExpanded, setInternalExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const groupContext = useCollapsibleGroup();

  // Determine which mode we're in
  const isInGroup = groupContext !== null && id !== undefined;
  const isControlled = controlledExpanded !== undefined;

  // Get the expanded state based on the mode
  const expanded = isInGroup ? groupContext.expandedId === id : isControlled ? controlledExpanded : internalExpanded;

  const handleToggle = (event: MouseEvent) => {
    event.stopPropagation();

    if (isInGroup) {
      // Use group context
      groupContext.toggleExpanded(id);
    } else if (isControlled) {
      // In controlled mode, call the provided onToggle callback
      onToggle?.(event);
    } else {
      // In uncontrolled mode, manage state internally
      const newExpanded = !expanded;
      setInternalExpanded(newExpanded);
    }
  };

  // Handle scrolling when expanded changes
  useEffect(() => {
    if (expanded) {
      const scrollToContent = () => {
        if (contentRef.current) {
          contentRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        } else {
          // Retry if content not ready yet
          setTimeout(scrollToContent, 100);
        }
      };

      // Start trying after Collapse animation begins
      setTimeout(scrollToContent, 200);
    }
  }, [expanded]);

  const defaultExpandCollapseButton = (
    <span>
      <IconButton
        size="small"
        disableRipple
        disableFocusRipple
        disableTouchRipple
        onClick={handleToggle}
        disabled={disableCollapse}
      >
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    </span>
  );

  const collapseControl =
    children && showCollapseArrow ? (
      collapseIcon ? (
        collapseIcon
      ) : hideCollapseExpandTooltip ? (
        defaultExpandCollapseButton
      ) : (
        <Tooltip placement="left" title={t('common.noDetail')} disableInteractive>
          {defaultExpandCollapseButton}
        </Tooltip>
      )
    ) : null;

  const iconsEndFiltered = iconsEnd.filter(Boolean);

  const renderActionIconButton = (button: IconButtonConfig, stopPropagation = true) => {
    if (button.unwrapped) {
      const content = button.icon;
      if (!button.tooltip) {
        return content;
      }
      return (
        <Tooltip placement="left" title={button.tooltip} disableInteractive>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            {content}
          </Box>
        </Tooltip>
      );
    }

    if (button.unwrapped) {
      return button.icon;
    }

    const iconButton = (
      <IconButton
        data-testid={button.testId}
        disabled={button.disabled}
        size={actionsRailWidth ? 'medium' : 'small'}
        color={button.color || 'default'}
        onClick={
          stopPropagation
            ? (e) => {
                e.stopPropagation();
                button.onClick?.();
              }
            : button.onClick
        }
        sx={{
          ...iconButtonIconSx,
          'color': button.color ? `${button.color}.main` : undefined,
          ...button.sx,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {button.icon}
      </IconButton>
    );

    if (!button.tooltip) {
      return iconButton;
    }

    return (
      <Tooltip placement="left" title={button.tooltip} disableInteractive>
        {button.disabled ? <span>{iconButton}</span> : iconButton}
      </Tooltip>
    );
  };

  const renderActionsRail = () => {
    const secondaryActions: Array<IconButtonConfig | 'iconEnd' | null> = (() => {
      if (headerActions?.length) {
        return headerActions;
      }
      const single = headerAction ?? actionButtons[0] ?? (iconsEndFiltered[0] ? ('iconEnd' as const) : null);
      return [null, single];
    })();
    const occupiedSecondary = secondaryActions.filter(Boolean);
    const railWidthPx = flowControlActionsRailWidthPx(occupiedSecondary.length);

    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        sx={{
          width: railWidthPx,
          minWidth: railWidthPx,
          maxWidth: railWidthPx,
          flexShrink: 0,
          height: FLOW_CONTROL_ROW_HEIGHT_PX,
        }}
      >
        {occupiedSecondary.map((secondaryContent, index) => (
          <Box key={index} sx={flowControlActionSlotSx}>
            {typeof secondaryContent === 'string'
              ? iconsEndFiltered[0]
              : secondaryContent
                ? renderActionIconButton(secondaryContent)
                : null}
          </Box>
        ))}
        <Box
          sx={{
            position: 'relative',
            width: FLOW_CONTROL_MEDIA_PLAY_SLOT_WIDTH_PX,
            minWidth: FLOW_CONTROL_MEDIA_PLAY_SLOT_WIDTH_PX,
            maxWidth: FLOW_CONTROL_MEDIA_PLAY_SLOT_WIDTH_PX,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            height: FLOW_CONTROL_ROW_HEIGHT_PX,
          }}
        >
          <Divider
            orientation="vertical"
            sx={{
              position: 'absolute',
              left: 0,
              top: 8,
              bottom: 8,
              height: 'auto',
            }}
          />
          {playActionButton ? renderActionIconButton(playActionButton) : null}
        </Box>
        {collapseControl}
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: '2px',
        cursor: 'pointer',
        backgroundColor: theme.palette.background.elevation2,
        ...sx,
      }}
    >
      <Stack
        sx={{
          minHeight: actionsRailWidth ? FLOW_CONTROL_ROW_HEIGHT_PX : 40,
          py: 0,
        }}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={actionsRailWidth ? 0 : 0.5}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexGrow={1} minWidth={0} onClick={onClick}>
          {leadingAdornment || iconStart ? (
            <Stack direction="row" alignItems="center" spacing={0} flexShrink={0}>
              {leadingAdornment ? (
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{leadingAdornment}</Box>
              ) : null}
              {iconStart ? (
                <Box
                  sx={{
                    'display': 'flex',
                    'alignItems': 'center',
                    'minWidth': leadingAdornment
                      ? 'auto'
                      : actionsRailWidth
                        ? FLOW_CONTROL_MEDIA_ACTION_SLOT_WIDTH_PX
                        : 34,
                    'width': 'auto',
                    'height': actionsRailWidth ? FLOW_CONTROL_ROW_HEIGHT_PX : 40,
                    'justifyContent': 'center',
                    'px': 0,
                    '& > svg': {
                      fontSize: actionsRailWidth ? '1.75rem' : '1.5rem',
                      width: actionsRailWidth ? '1.75rem' : '1.5rem',
                      height: actionsRailWidth ? '1.75rem' : '1.5rem',
                    },
                    '& > *': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {iconStart}
                </Box>
              ) : null}
            </Stack>
          ) : null}
          {title &&
            (typeof title === 'string' ? (
              <Typography
                variant="body2"
                flexGrow={1}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  ...(actionsRailWidth
                    ? { whiteSpace: 'nowrap' }
                    : {
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }),
                }}
              >
                {title}
              </Typography>
            ) : (
              <Box flexGrow={1} sx={{ flex: 1, minWidth: 0, width: '100%', overflow: 'hidden', display: 'flex' }}>
                {title}
              </Box>
            ))}
        </Stack>

        {!isEditingTitle ? (
          actionsRailWidth ? (
            renderActionsRail()
          ) : (
            <Stack direction="row" alignItems="center" spacing={0}>
              {(headerActions?.length ? headerActions : headerAction ? [headerAction] : []).map((action, index) => (
                <Box
                  key={action.testId ?? `header-action-${index}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: 34,
                    height: 34,
                    justifyContent: 'center',
                  }}
                >
                  {renderActionIconButton(action)}
                </Box>
              ))}
              {iconsEndFiltered.map((icon, index) => (
                <Box
                  key={index}
                  sx={{
                    'display': 'flex',
                    'alignItems': 'center',
                    'width': 34,
                    'height': 34,
                    'justifyContent': 'center',
                    '& > svg': {
                      fontSize: '1.5rem',
                      width: '1.5rem',
                      height: '1.5rem',
                    },
                  }}
                >
                  {icon}
                </Box>
              ))}
              {actionButtons.map((button, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                  {button.unwrapped ? (
                    renderActionIconButton(button)
                  ) : (
                    <Tooltip placement="left" title={button.tooltip} disableInteractive>
                      {button.disabled ? (
                        <span>
                          <IconButton
                            data-testid={button.testId}
                            disabled={button.disabled}
                            size="small"
                            color={button.color || 'default'}
                            onClick={(e) => {
                              e.stopPropagation();
                              button.onClick?.();
                            }}
                            sx={{
                              '& > svg': {
                                fontSize: '1.5rem',
                                width: '1.5rem',
                                height: '1.5rem',
                              },
                              ...button.sx,
                            }}
                          >
                            {button.icon}
                          </IconButton>
                        </span>
                      ) : (
                        <IconButton
                          data-testid={button.testId}
                          disabled={button.disabled}
                          size="small"
                          color={button.color || 'default'}
                          onClick={(e) => {
                            e.stopPropagation();
                            button.onClick?.();
                          }}
                          sx={{
                            '& > svg': {
                              fontSize: '1.5rem',
                              width: '1.5rem',
                              height: '1.5rem',
                            },
                            ...button.sx,
                          }}
                        >
                          {button.icon}
                        </IconButton>
                      )}
                    </Tooltip>
                  )}
                </Box>
              ))}
              {playActionButton ? (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.25, alignSelf: 'stretch' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip placement="left" title={playActionButton.tooltip} disableInteractive>
                      {playActionButton.disabled ? (
                        <span>
                          <IconButton
                            data-testid={playActionButton.testId}
                            disabled={playActionButton.disabled}
                            size="small"
                            color={playActionButton.color || 'default'}
                            onClick={(e) => {
                              e.stopPropagation();
                              playActionButton.onClick?.();
                            }}
                            sx={{
                              '& > svg': {
                                fontSize: '1.5rem',
                                width: '1.5rem',
                                height: '1.5rem',
                              },
                              ...playActionButton.sx,
                            }}
                          >
                            {playActionButton.icon}
                          </IconButton>
                        </span>
                      ) : (
                        <IconButton
                          data-testid={playActionButton.testId}
                          disabled={playActionButton.disabled}
                          size="small"
                          color={playActionButton.color || 'default'}
                          onClick={(e) => {
                            e.stopPropagation();
                            playActionButton.onClick?.();
                          }}
                          sx={{
                            '& > svg': {
                              fontSize: '1.5rem',
                              width: '1.5rem',
                              height: '1.5rem',
                            },
                            ...playActionButton.sx,
                          }}
                        >
                          {playActionButton.icon}
                        </IconButton>
                      )}
                    </Tooltip>
                  </Box>
                </>
              ) : null}
              {collapseControl}
            </Stack>
          )
        ) : (
          collapseControl
        )}
      </Stack>

      {children && (
        <Collapse in={expanded} timeout={200} unmountOnExit>
          <Box ref={contentRef} sx={{ backgroundColor: theme.palette.background.elevation1 }}>
            {children}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};
